/**
 * Tasks Redux Slice - State management for deal tasks
 *
 * Manages the application state for tasks including loading states,
 * error handling, and CRUD operations.
 */

import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit'
import {
  fetchTasksForDeal,
  createTask as createTaskAPI,
  updateTask as updateTaskAPI,
  deleteTask as deleteTaskAPI,
  type TaskRecordType
} from '../../app_modules/tasks/services/tasksService.ts'
import { reorderTasks as reorderTasksAPI } from '../../app_modules/deals/services/tasksService.ts'
import { DealFields } from '../../state_manager/data_models/deal_record.ts'
import { TaskRecordFields, type TaskRecord } from '../../state_manager/data_models/task_record.ts'
import { mergeTaskUpdate, taskDisplaySubject } from '../../app_modules/tasks/utils/mergeTaskUpdate.ts'
import { selectDealById } from '../../state_manager/state_update_actions/deals/selectors.ts'
import { selectCompanyById } from '../../state_manager/state_update_actions/companies/selectors.ts'
import { selectActiveRecordById } from '../../state_manager/state_update_actions/active_records/selectors.ts'
import { AccountRecordFields } from '../../state_manager/data_models/account_record.ts'
import type { AuthState } from '../../core/auth/authSlice.ts'
import type { MainState } from '../../state_manager/interfaces/deals.ts'
import type { CompanyState } from '../../state_manager/interfaces/companies.ts'
import type { ActiveRecordsState } from '../../state_manager/interfaces/active_records.ts'
import type { FundraisingState } from '../../state_manager/interfaces/fundraising.ts'

export interface TasksState {
  /** Tasks keyed by the id of the deal they were created or reordered under. */
  tasksByDeal: Record<string, TaskRecord[]>
  /** Which deals have been fetched. */
  fetchedDeals: string[]
  loading: boolean
  error: string | null
  submitting: boolean
  submitError: string | null
}

/** The slices of the store the task thunks read (through the module selectors). */
type TasksThunkState = {
  auth: AuthState
  tasks: TasksState
  deals: MainState
  companies: CompanyState
  activeRecords: ActiveRecordsState
  fundraising: FundraisingState
}
type TasksThunkConfig = { state: TasksThunkState; rejectValue: string }

type TaskLike = Record<string, unknown>

function taskRowMatchesId(task: TaskLike | null | undefined, taskId: string | number | null | undefined): boolean {
  if (task == null || taskId == null) return false
  const idStr = String(taskId)
  const candidates = [task[TaskRecordFields.tasks__id], task.id].filter((v) => v != null && v !== '')
  return candidates.some((c) => String(c) === idStr)
}

function filterTasksRemovingId<T extends TaskLike>(tasks: T[] | null | undefined, taskId: string): T[] {
  if (!Array.isArray(tasks)) return []
  return tasks.filter((t) => !taskRowMatchesId(t, taskId))
}

/** The services throw plain Errors (fetch), so the message is all there is to report. */
function describeError(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback
}

// Service functions are imported from tasksService.ts for CRUD operations
// Reorder still uses deals service as it matches router.py route: /deals/{deal_id}/tasks/reorder

// Initial state
const initialState: TasksState = {
  tasksByDeal: {},
  fetchedDeals: [],
  loading: false,
  error: null,
  submitting: false,
  submitError: null
}

// Async thunks
const fetchTasks = createAsyncThunk<{ dealId: string; tasks: TaskRecord[] }, string, TasksThunkConfig>(
  'tasks/fetchTasks',
  async (dealId, { rejectWithValue, getState }) => {
    try {
      const accessToken = getState().auth.accessToken

      if (!accessToken) {
        return rejectWithValue('No access token available')
      }

      const tasks = await fetchTasksForDeal(dealId, undefined, accessToken)

      return {
        dealId,
        tasks: tasks || []
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error)
      return rejectWithValue(describeError(error, 'Failed to fetch tasks'))
    }
  }
)

export const createTask = createAsyncThunk<
  { dealId: string; task: TaskRecord },
  { dealId: string; taskData: unknown; recordType?: TaskRecordType; accountId?: string | null },
  TasksThunkConfig
>(
  'tasks/createTask',
  async ({ dealId, taskData, recordType = 'deal', accountId }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState()
      const accessToken = state.auth.accessToken

      if (!accessToken) {
        return rejectWithValue('No access token available')
      }

      const taskResponse = await createTaskAPI(dealId, taskData, accessToken, recordType, accountId)

      // A row that arrives without a subject shows the placeholder rather than nothing
      const task: TaskRecord = { ...taskResponse, [TaskRecordFields.tasks__subject]: taskDisplaySubject(taskResponse) }

      // Update state based on recordType
      if (recordType === 'company') {
        // Update companies state to reflect the new task
        const { updateCompanyRecord } = await import('../../state_manager/state_update_actions/companies/index.ts')

        // Get existing tasks from the company record - check both companies.data and activeRecords
        const companyRecord = selectCompanyById(state, dealId) || selectActiveRecordById(state, dealId)
        if (!companyRecord) {
          console.warn(
            `Company record not found in Redux state (companies.data or activeRecords) for companyId: ${dealId}`
          )
          return {
            dealId,
            task
          }
        }

        const existingTasks = companyRecord[AccountRecordFields.prog__tasks] || []
        const updatedTasks = [task, ...existingTasks]

        // Update companies state
        dispatch(
          updateCompanyRecord({
            companyId: dealId,
            updates: {
              [AccountRecordFields.prog__tasks]: updatedTasks
            }
          })
        )
      } else {
        // Update deals state to reflect the new task
        const { updateDealRecord } = await import('../../state_manager/state_update_actions/deals/deal_actions.ts')

        // Get existing tasks from Redux state for local state update
        const existingTasks = state.tasks.tasksByDeal[dealId] || []
        const updatedTasks = [task, ...existingTasks]

        // Update deals state (pipeline and deal data arrays)
        dispatch(
          updateDealRecord({
            dealId,
            updates: {
              [DealFields.prog__tasks]: updatedTasks
            }
          })
        )
      }

      return {
        dealId,
        task
      }
    } catch (error) {
      console.error('Failed to create task:', error)
      return rejectWithValue(describeError(error, 'Failed to create task'))
    }
  }
)

export const updateTask = createAsyncThunk<
  { dealId: string; task: TaskRecord },
  { dealId: string; taskId: string; taskData: unknown; recordType?: TaskRecordType },
  TasksThunkConfig
>(
  'tasks/updateTask',
  async ({ dealId, taskId, taskData, recordType = 'deal' }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState()
      const accessToken = state.auth.accessToken

      if (!accessToken) {
        return rejectWithValue('No access token available')
      }

      // PUT /tasks/{id} echoes only the fields that were sent (a completion
      // toggle sends id + completed), so this is a patch: it is merged over the
      // task already held in the record below, never treated as the whole task.
      const task: TaskRecord = { ...(await updateTaskAPI(dealId, taskId, taskData, accessToken, recordType)) }
      let updatedTask: TaskRecord = task
      // Use task ID from response as primary source, fallback to parameter
      const taskIdFromResponse = task[TaskRecordFields.tasks__id] || taskId
      const isTargetTask = (t: TaskLike) => taskRowMatchesId(t, taskIdFromResponse) || taskRowMatchesId(t, taskId)

      // Update state based on recordType
      if (recordType === 'company') {
        // Update companies state to reflect the updated task
        const { updateCompanyRecord } = await import('../../state_manager/state_update_actions/companies/index.ts')

        // Get existing tasks from the company record - check both companies.data and activeRecords
        const companyRecord = selectCompanyById(state, dealId) || selectActiveRecordById(state, dealId)
        if (!companyRecord) {
          console.warn(
            `Company record not found in Redux state (companies.data or activeRecords) for companyId: ${dealId}`
          )
          return {
            dealId,
            task
          }
        }

        const existingTasks: TaskRecord[] = companyRecord[AccountRecordFields.prog__tasks] || []
        const updatedTasks = existingTasks.map((t) => {
          if (isTargetTask(t)) {
            updatedTask = mergeTaskUpdate(t, task)
            return updatedTask
          }
          return t
        })

        // Update companies state
        dispatch(
          updateCompanyRecord({
            companyId: dealId,
            updates: {
              [AccountRecordFields.prog__tasks]: updatedTasks
            }
          })
        )
      } else if (recordType === 'prospect') {
        // Update prospects state to reflect the updated task
        const { updateProspectRecord } = await import('../../state_manager/state_update_actions/fundraising/index.ts')
        const { selectProspectById } = await import('../../state_manager/state_update_actions/fundraising/selectors.ts')
        const { ProspectFields } = await import('../../app_modules/fundraising/utils/fieldConfigs.ts')

        // Get existing tasks from the prospect record (activeRecords when the
        // drawer shows a prospect the grid never loaded), not from tasks slice
        const prospectRecord = selectProspectById(state, dealId) || selectActiveRecordById(state, dealId)
        if (!prospectRecord) {
          console.warn(`Prospect record not found in Redux state for prospectId: ${dealId}`)
          return {
            dealId,
            task
          }
        }

        const existingTasks: TaskRecord[] = prospectRecord[ProspectFields.prog__tasks] || []
        const updatedTasks = existingTasks.map((t) => {
          if (isTargetTask(t)) {
            updatedTask = mergeTaskUpdate(t, task)
            return updatedTask
          }
          return t
        })

        // Update prospects state
        // Always write to 'prog__tasks' (correct field name per schema)
        // TasksSection reads from both 'prog__tasks' and 'tasks', preferring 'prog__tasks'
        dispatch(
          updateProspectRecord({
            prospectId: dealId,
            updates: {
              [ProspectFields.prog__tasks]: updatedTasks
            }
          })
        )
      } else {
        // Update deals state to reflect the updated task
        const { updateDealRecord } = await import('../../state_manager/state_update_actions/deals/deal_actions.ts')

        // Get existing tasks from the deal record, not from tasks slice. The
        // drawer can show a deal the grid and the pipeline never loaded (a
        // Passed deal older than the column's max age, a search result), which
        // lives in activeRecords only; without that fallback this branch found
        // nothing, wrote an EMPTY task list onto the record and the drawer
        // showed "0 tasks" after a toggle.
        const dealRecord = selectDealById(state, dealId) || selectActiveRecordById(state, dealId)
        if (!dealRecord) {
          console.warn(`Deal record not found in Redux state (deals or activeRecords) for dealId: ${dealId}`)
          return {
            dealId,
            task
          }
        }
        const existingTasks: TaskRecord[] = dealRecord[DealFields.prog__tasks] || []
        const updatedTasks = existingTasks.map((t) => {
          if (isTargetTask(t)) {
            updatedTask = mergeTaskUpdate(t, task)
            return updatedTask
          }
          return t
        })

        // Update deals state (pipeline and deal data arrays)
        dispatch(
          updateDealRecord({
            dealId,
            updates: {
              [DealFields.prog__tasks]: updatedTasks
            }
          })
        )
      }

      return {
        dealId,
        task: updatedTask
      }
    } catch (error) {
      console.error('Failed to update task:', error)
      return rejectWithValue(describeError(error, 'Failed to update task'))
    }
  }
)

export const deleteTask = createAsyncThunk<
  { dealId: string; taskId: string },
  { dealId: string; taskId: string; recordType?: TaskRecordType },
  TasksThunkConfig
>('tasks/deleteTask', async ({ dealId, taskId, recordType = 'deal' }, { rejectWithValue, getState, dispatch }) => {
  try {
    const state = getState()
    const accessToken = state.auth.accessToken

    if (!accessToken) {
      return rejectWithValue('No access token available')
    }

    await deleteTaskAPI(dealId, taskId, accessToken)

    // Every record kind keeps its tasks under prog__tasks (AccountRecordFields,
    // DealFields and ProspectFields all spell the same key).
    const applyActiveRecordTaskRemoval = async () => {
      const { updateActiveRecord } = await import(
        '../../state_manager/state_update_actions/active_records/active_record_actions.ts'
      )
      const rec = selectActiveRecordById(state, dealId)
      if (!rec) return
      const arr = rec[DealFields.prog__tasks]
      if (Array.isArray(arr)) {
        dispatch(
          updateActiveRecord({
            uuid: String(dealId),
            updates: { [DealFields.prog__tasks]: filterTasksRemovingId(arr, taskId) }
          })
        )
      }
    }

    if (recordType === 'company') {
      const { updateCompanyRecord } = await import('../../state_manager/state_update_actions/companies/index.ts')
      const companyRecord = selectCompanyById(state, dealId) || selectActiveRecordById(state, dealId)
      if (companyRecord) {
        const existingTasks: TaskRecord[] = companyRecord[AccountRecordFields.prog__tasks] || []
        dispatch(
          updateCompanyRecord({
            companyId: dealId,
            updates: {
              [AccountRecordFields.prog__tasks]: filterTasksRemovingId(existingTasks, taskId)
            }
          })
        )
      }
      await applyActiveRecordTaskRemoval()
    } else if (recordType === 'prospect') {
      const { updateProspectRecord } = await import('../../state_manager/state_update_actions/fundraising/index.ts')
      const { selectProspectById } = await import('../../state_manager/state_update_actions/fundraising/selectors.ts')
      const { ProspectFields } = await import('../../app_modules/fundraising/utils/fieldConfigs.ts')
      const prospectRecord = selectProspectById(state, dealId) || selectActiveRecordById(state, dealId)
      if (prospectRecord) {
        const existingTasks: TaskRecord[] = prospectRecord[ProspectFields.prog__tasks] || []
        dispatch(
          updateProspectRecord({
            prospectId: dealId,
            updates: {
              [ProspectFields.prog__tasks]: filterTasksRemovingId(existingTasks, taskId)
            }
          })
        )
      }
      await applyActiveRecordTaskRemoval()
    } else {
      const { updateDealRecord } = await import('../../state_manager/state_update_actions/deals/deal_actions.ts')
      const dealRecord = selectDealById(state, dealId)
      if (dealRecord) {
        const fromDeal = dealRecord[DealFields.prog__tasks]
        const fromSlice = state.tasks.tasksByDeal[dealId]
        let existingTasks: TaskRecord[] = []
        if (Array.isArray(fromDeal) && fromDeal.length > 0) {
          existingTasks = fromDeal
        } else if (Array.isArray(fromSlice) && fromSlice.length > 0) {
          existingTasks = fromSlice
        } else if (Array.isArray(fromDeal)) {
          existingTasks = fromDeal
        } else if (Array.isArray(fromSlice)) {
          existingTasks = fromSlice
        }
        dispatch(
          updateDealRecord({
            dealId,
            updates: {
              [DealFields.prog__tasks]: filterTasksRemovingId(existingTasks, taskId)
            }
          })
        )
      }
      await applyActiveRecordTaskRemoval()
    }

    return {
      dealId,
      taskId
    }
  } catch (error) {
    console.error('Failed to delete task:', error)
    return rejectWithValue(describeError(error, 'Failed to delete task'))
  }
})

export const reorderTasks = createAsyncThunk<
  { dealId: string },
  { dealId: string; taskOrders: Array<{ id: string; sort_order: number }> },
  TasksThunkConfig
>('tasks/reorderTasks', async ({ dealId, taskOrders }, { rejectWithValue, getState }) => {
  try {
    const accessToken = getState().auth.accessToken

    if (!accessToken) {
      return rejectWithValue('No access token available')
    }

    await reorderTasksAPI(dealId, taskOrders, accessToken)

    return {
      dealId
    }
  } catch (error) {
    console.error('Failed to reorder tasks:', error)
    return rejectWithValue(describeError(error, 'Failed to reorder tasks'))
  }
})

// Create the slice
const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
      state.submitError = null
    },

    clearTasksForDeal: (state, action: PayloadAction<string>) => {
      delete state.tasksByDeal[action.payload]
      state.fetchedDeals = state.fetchedDeals.filter((id) => id !== action.payload)
    },

    // Optimistic update for task completion toggle
    toggleTaskCompleted: (state, action: PayloadAction<{ dealId: string; taskId: string }>) => {
      const { dealId, taskId } = action.payload
      const tasks = state.tasksByDeal[dealId]

      if (tasks) {
        const task = tasks.find((t) => taskRowMatchesId(t, taskId))
        if (task) {
          task[TaskRecordFields.tasks__completed] = !task[TaskRecordFields.tasks__completed]
          if (task[TaskRecordFields.tasks__completed]) {
            task[TaskRecordFields.tasks__completed_on] = new Date().toISOString().split('T')[0]
          } else {
            task[TaskRecordFields.tasks__completed_on] = null
          }
        }
      }
    },

    // Local reorder for immediate UI feedback
    localReorderTasks: (state, action: PayloadAction<{ dealId: string; newTaskOrder: TaskRecord[] }>) => {
      const { dealId, newTaskOrder } = action.payload
      state.tasksByDeal[dealId] = newTaskOrder
    },

    // Pre-populate tasks from existing deal data (when available from deals table)
    seedTasksFromDeal: (state, action: PayloadAction<{ dealId: string; tasks: TaskRecord[] | null | undefined }>) => {
      const { dealId, tasks } = action.payload

      // Only populate if we don't already have tasks for this deal
      if (!state.tasksByDeal[dealId] && tasks && Array.isArray(tasks)) {
        state.tasksByDeal[dealId] = tasks
        if (!state.fetchedDeals.includes(dealId)) {
          state.fetchedDeals.push(dealId)
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch tasks
      .addCase(fetchTasks.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.loading = false
        state.tasksByDeal[action.payload.dealId] = action.payload.tasks
        if (!state.fetchedDeals.includes(action.payload.dealId)) {
          state.fetchedDeals.push(action.payload.dealId)
        }
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'Failed to fetch tasks'
      })

      // Create task
      .addCase(createTask.pending, (state) => {
        state.submitting = true
        state.submitError = null
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.submitting = false
        const { dealId, task } = action.payload

        if (!state.tasksByDeal[dealId]) {
          state.tasksByDeal[dealId] = []
        }

        // Add new task at the beginning of the array (top of the list)
        state.tasksByDeal[dealId].unshift(task)
      })
      .addCase(createTask.rejected, (state, action) => {
        state.submitting = false
        state.submitError = action.payload ?? 'Failed to create task'
      })

      // Update task
      .addCase(updateTask.pending, (state) => {
        state.submitting = true
        state.submitError = null
      })
      .addCase(updateTask.fulfilled, (state, action) => {
        state.submitting = false
        const { dealId, task } = action.payload

        if (state.tasksByDeal[dealId]) {
          const index = state.tasksByDeal[dealId].findIndex((t) =>
            taskRowMatchesId(t, task[TaskRecordFields.tasks__id])
          )
          if (index !== -1) {
            state.tasksByDeal[dealId][index] = mergeTaskUpdate(state.tasksByDeal[dealId][index], task)
          }
        }
      })
      .addCase(updateTask.rejected, (state, action) => {
        state.submitting = false
        state.submitError = action.payload ?? 'Failed to update task'
      })

      // Delete task
      .addCase(deleteTask.pending, (state) => {
        state.submitting = true
        state.submitError = null
      })
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.submitting = false
        const { dealId, taskId } = action.payload

        if (state.tasksByDeal[dealId]) {
          state.tasksByDeal[dealId] = state.tasksByDeal[dealId].filter((t) => !taskRowMatchesId(t, taskId))
        }
      })
      .addCase(deleteTask.rejected, (state, action) => {
        state.submitting = false
        state.submitError = action.payload ?? 'Failed to delete task'
      })

      // Reorder tasks
      .addCase(reorderTasks.pending, () => {
        // Don't show loading for reorder as it should be instant
      })
      .addCase(reorderTasks.fulfilled, () => {
        // The API doesn't return the updated tasks, but the localReorderTasks
        // action has already optimistically updated the UI, so we don't need
        // to do anything here
      })
      .addCase(reorderTasks.rejected, (state, action) => {
        state.error = action.payload ?? 'Failed to reorder tasks'
        // On error, we should refetch the tasks to restore the correct order
        // but for now we'll just show the error
      })
  }
})

// Export actions
export const { toggleTaskCompleted, localReorderTasks } = tasksSlice.actions
const { clearError, clearTasksForDeal, seedTasksFromDeal } = tasksSlice.actions

type TasksRoot = { tasks: TasksState }

// Selectors
const EMPTY_TASKS_ARRAY: TaskRecord[] = []
const selectTasksForDeal = (state: TasksRoot, dealId: string) => state.tasks.tasksByDeal[dealId] || EMPTY_TASKS_ARRAY
const selectTasksLoading = (state: TasksRoot) => state.tasks.loading
const selectTasksError = (state: TasksRoot) => state.tasks.error
const selectTasksSubmitting = (state: TasksRoot) => state.tasks.submitting
const selectTasksSubmitError = (state: TasksRoot) => state.tasks.submitError

const selectTasksFetched = (state: TasksRoot, dealId: string) => state.tasks.fetchedDeals.includes(dealId)

// Helper selectors
const selectIncompleteTasksForDeal = (state: TasksRoot, dealId: string) => {
  const tasks = selectTasksForDeal(state, dealId)
  return tasks.filter((task) => !task[TaskRecordFields.tasks__completed])
}

const selectCompletedTasksForDeal = (state: TasksRoot, dealId: string) => {
  const tasks = selectTasksForDeal(state, dealId)
  return tasks.filter((task) => task[TaskRecordFields.tasks__completed])
}

const selectTasksCountForDeal = (state: TasksRoot, dealId: string) => {
  const tasks = selectTasksForDeal(state, dealId)
  return {
    total: tasks.length,
    completed: tasks.filter((task) => task[TaskRecordFields.tasks__completed]).length,
    incomplete: tasks.filter((task) => !task[TaskRecordFields.tasks__completed]).length
  }
}

export default tasksSlice.reducer
